<script lang="ts" context="module">
    export interface PillOption {
        value: string;
        label: string;
        count?: number;
    }
</script>

<script lang="ts">
    import { createEventDispatcher } from 'svelte';

    export let label: string = 'Status';
    export let options: PillOption[] = [];
    export let selected: string[] = [];

    const dispatch = createEventDispatcher<{ change: string[] }>();

    /** At most one option besides “All”; click the active pill again to clear (All). */
    function toggle(value: string) {
        if (selected.length === 1 && selected[0] === value) {
            selected = [];
        } else {
            selected = [value];
        }
        dispatch('change', selected);
    }

    function clear() {
        selected = [];
        dispatch('change', selected);
    }

    $: allSelected = selected.length === 0;
</script>

<div class="pill-group" role="group" aria-label={label}>
    <span class="pill-label">{label}:</span>
    <button
        type="button"
        class="pill"
        class:active={allSelected}
        on:click={clear}
    >All</button>
    {#each options as opt (opt.value)}
        <button
            type="button"
            class="pill"
            class:active={selected.includes(opt.value)}
            on:click={() => toggle(opt.value)}
        >
            {opt.label}{#if opt.count != null}<span class="count">{opt.count}</span>{/if}
        </button>
    {/each}
</div>

<style>
    .pill-group {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex-wrap: wrap;
    }

    .pill-label {
        font-size: var(--text-sm);
        color: var(--grey-secondary);
        margin-right: var(--space-1);
    }

    .pill {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding: .25rem .65rem;
        border: 1px solid var(--border-color);
        border-radius: var(--radius-pill);
        background: var(--surface);
        color: var(--grey-secondary);
        font: inherit;
        font-size: var(--text-sm);
        cursor: pointer;
        transition: background .12s, border-color .12s, color .12s;
    }

    .pill:hover { background: var(--surface-hover); color: var(--grey); }

    .pill.active {
        background: var(--primary-soft, var(--status-info-soft));
        border-color: var(--primary);
        color: var(--primary);
        font-weight: 500;
    }

    .pill:focus-visible { outline: none; box-shadow: var(--focus-ring); }

    .count {
        font-size: var(--text-xs);
        color: inherit;
        opacity: .7;
        font-variant-numeric: tabular-nums;
    }
</style>
